<template>
  <b-modal
    :id="id"
    :title="trans('age_constraint')"
    :ok-title="trans('save')"
    :cancel-title="trans('cancel')"
    size="lg"
    @shown="onShown"
    @ok="onSave"
  >
    <b-row>
      <b-col class="col-7">
        <b-tabs v-model="tab" content-class="mt-3">
          <b-tab :title="trans('age')">
            <label class="d-block">
              <strong>{{ trans('min_age') }}</strong>
            </label>
            <b-row>
              <b-col>
                <b-form-group :label="trans('years')">
                  <b-form-input type="number" min="0" step="1" v-model="min_years" />
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group :label="trans('months')">
                  <b-form-input type="number" min="0" step="1" v-model="min_months" />
                </b-form-group>
              </b-col>
            </b-row>

            <label class="d-block">
              <strong>{{ trans('max_age_less_than') }}</strong>
            </label>
            <b-row>
              <b-col>
                <b-form-group :label="trans('years')">
                  <b-form-input type="number" min="0" step="1" v-model="max_years" />
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group :label="trans('months')">
                  <b-form-input type="number" min="0" step="1" v-model="max_months" />
                </b-form-group>
              </b-col>
            </b-row>

            <b-form-group :label="trans('at_the_start_of_the_class')">
              <b-form-checkbox switch v-model="age_at_start" :disabled="age_at_end_of_the_year">
                {{ trans(age_at_start ? 'yes' : 'no') }}
              </b-form-checkbox>
            </b-form-group>

            <b-form-group :label="trans('age_at_end_of_the_year')">
              <b-form-checkbox switch v-model="age_at_end_of_the_year" :disabled="age_at_start">
                {{ trans(age_at_end_of_the_year ? 'yes' : 'no') }}
              </b-form-checkbox>
            </b-form-group>

            <b-form-group :label="trans('age_constraint_date_of')" v-if="!age_at_start && !age_at_end_of_the_year">
              <datetime v-model="date_of" :config="dateOptions"></datetime>
              <b-form-text>
                {{ trans('age_constraint_date_of_about') }}
              </b-form-text>
            </b-form-group>
            
          </b-tab>

          <b-tab v-if="!hideDateOfBirth" :title="trans('date_of_birth')">
            <b-row>
              <b-col>
                <b-form-group :label="trans('minimum')">
                  <datetime v-model="min_date_of_birth" :config="dateOptions"></datetime>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group :label="trans('maximum')">
                  <datetime v-model="max_date_of_birth" :config="dateOptions"></datetime>
                </b-form-group>
              </b-col>
            </b-row>
          </b-tab>
        </b-tabs>

        <b-form-group :label="trans('note')">
          <b-form-textarea v-model="note"></b-form-textarea>
        </b-form-group>

        <p v-if="!!humanized">
          {{ humanized }}
        </p>
      </b-col>

      <b-col class="col-5">
        <b-card class="mt-4">
          <p><strong>{{ trans('bot_run_simulation') }}</strong></p>
          <p v-if="season"><small class="text-muted">{{ trans('for_season') }} {{ season }}</small></p>
          <b-form-group :label="trans('date_of_birth')">
            <datetime v-model="sim_date_of_birth" :config="dateOptions"></datetime>
          </b-form-group>
          <b-form-group v-if="!!simAge" :label="trans('age')">
            <div class="form-control">
              <b-row>
                <b-col>
                  {{ simAge }}
                </b-col>
                <b-col class="text-right">
                  <div v-if="simValid === true" class="text-success">
                    <i class="fa fa-fw fa-check"></i>
                    {{ trans('valid') }}
                  </div>
                  <div v-if="simValid === false" class="text-danger">
                    <i class="fa fa-fw fa-exclamation-triangle"></i>
                    {{ trans('failed') }}
                  </div>
                </b-col>
              </b-row>
            </div>
            <b-form-text v-if="simValid === false">
              <span class="text-danger">{{ humanized }}</span>
            </b-form-text>
          </b-form-group>
        </b-card>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
  import Datetime from 'vue-bootstrap-datetimepicker'

  export default {
    props: {
      id: {
        type: String,
        default: 'age-constraint-modal',
      },
      organization: Object,
      value: Object, // min, max, date_of
      hideDateOfBirth: {
        type: Boolean,
        default: false,
      },
      season: {
        type: Number,
        default: null
      }
    },
    components: {
      Datetime,
    },
    data() {
      return {
        trans: trans,
        moment: moment,
        tab: 0,
        min_years: null,
        min_months: null,
        max_years: null,
        max_months: null,
        date_of: null,
        dateOptions: {
          locale: window.lang,
          format: 'LL',
          showClear: true,
          viewMode: 'years',
          widgetPositioning: {
            horizontal: 'auto',
            vertical: 'bottom',
          },
        },
        use_min_max_date_of_birth: false,
        min_date_of_birth: null,
        max_date_of_birth: null,
        note: null,
        sim_date_of_birth: null,
        age_at_start: false,
        age_at_end_of_the_year: false,
      }
    },
    computed: {
      serialized() {
        return {
          min_years: this.min_years !== null ? parseInt(this.min_years, 10) : null,
          min_months: this.min_months !== null ? parseInt(this.min_months, 10) : null,
          max_years: this.max_years !== null ? parseInt(this.max_years, 10) : null,
          max_months: this.max_months !== null ? parseInt(this.max_months, 10) : null,
          date_of: this.date_of ? moment(this.date_of, 'LL').toISOString() : null,
          use_min_max_date_of_birth: this.use_min_max_date_of_birth,
          min_date_of_birth: this.min_date_of_birth ? moment(this.min_date_of_birth, 'LL').toISOString() : null,
          max_date_of_birth: this.max_date_of_birth ? moment(this.max_date_of_birth, 'LL').toISOString() : null,
          note: this.note,
          age_at_start: this.age_at_start,
          age_at_end_of_the_year: this.age_at_end_of_the_year,
        }
      },
      humanized() {
        return _.printAgeConstraint(this.serialized, this.organization)
      },
      simDateOfBirthMoment() {
        if (!this.sim_date_of_birth) return null
        return moment(this.sim_date_of_birth, 'LL').toISOString()
      },
      simAge() {
        if (!this.simDateOfBirthMoment) return null
        return _.printAge(this.simDateOfBirthMoment, this.serialized.date_of)
      },
      simValid() {
        if (!this.simDateOfBirthMoment || !this.humanized) return null
        return _.validateAgeConstraint(this.simDateOfBirthMoment, this.serialized, this.season)
      },
    },
    watch: {
      tab() {
        if (this.use_min_max_date_of_birth != !!this.tab)
          this.use_min_max_date_of_birth = this.tab == 1
      },
      age_at_end_of_the_year() {
        if (this.age_at_end_of_the_year)
          this.age_at_start = false
      },
      age_at_start() {
        if (this.age_at_start)
          this.age_at_end_of_the_year = false
      },
    },
    methods: {
      onShown() {
        this.min_years = this.value ? this.value.min_years : null
        this.min_months = this.value ? this.value.min_months : null
        this.max_years = this.value ? this.value.max_years : null
        this.max_months = this.value ? this.value.max_months : null
        this.date_of = this.value && this.value.date_of ? moment(this.value.date_of).format('LL') : null
        this.min_date_of_birth = this.value && this.value.min_date_of_birth ? moment(this.value.min_date_of_birth).format('LL') : null
        this.max_date_of_birth = this.value && this.value.max_date_of_birth ? moment(this.value.max_date_of_birth).format('LL') : null
        this.use_min_max_date_of_birth = this.value ? this.value.use_min_max_date_of_birth || false : false
        this.note = this.value ? this.value.note || null : null
        this.tab = this.use_min_max_date_of_birth ? 1 : 0
        this.age_at_start = this.value ? this.value.age_at_start : false
        this.age_at_end_of_the_year = this.value ? this.value.age_at_end_of_the_year : false
      },
      onSave(e) {
        this.$emit('input', this.serialized)
      },
    },
    mounted() {
      window.ageConstraintModal = this
    },
  }
</script>